<template lang="pug">
//- A till roll is 80mm of paper. Previewing it across half the screen tells the merchant nothing
//- about how it will actually print, so thermal templates get a receipt-width drawer - the same
//- widths the purchase form uses for its receipt vs its document preview.
el-drawer.document-template-preview-drawer(
    :title="drawerTitle"
    :visible="visible"
    direction="rtl"
    :size="isThermal ? '380px' : '40%'"
    :class="{ 'is-thermal': isThermal }"
    :before-close="handleClose"
)
    .drawer-body(v-if="template")

        //- Toolbar
        .preview-toolbar
            el-tag.format-tag(size="small" :type="formatTagType") {{ formatLabel }}

            template(v-if="renderConfig && useSynthetic")
                el-tag.sample-tag(size="small" type="info" effect="plain") {{ $t('sample-data') }}

            template(v-else-if="renderConfig")
                span.doc-label {{ entityLabel }}:
                el-select.entity-select(
                    v-model="documentId"
                    filterable
                    remote
                    clearable
                    :remote-method="searchEntities"
                    :loading="searching"
                    :placeholder="entityPlaceholder"
                    size="mini"
                    @change="onEntitySelected"
                )
                    el-option(
                        v-if="customIdValue"
                        key="__custom__"
                        :label="$t('opt-custom-id', { id: customIdValue })"
                        :value="customIdValue"
                    )
                    el-option-group(:label="$t('opt-group-recent')")
                        el-option(
                            v-for="opt in entityOptions"
                            :key="opt.id"
                            :label="opt.label"
                            :value="opt.id"
                        )

            i.el-icon-loading.preview-spin(v-if="loading")

        //- Content area
        .preview-content

            .preview-loading(v-if="loading")
                i.el-icon-loading
                span {{ $t('Rendering…') }}

            el-alert.preview-error(
                v-else-if="error"
                type="error"
                :title="error"
                :closable="false"
                show-icon
            )

            .preview-empty(v-else-if="renderConfig && noRecords")
                i.el-icon-folder-opened
                p {{ $t('no-sample-record') }}

            .preview-empty(v-else-if="renderConfig && !renderedHtml && !pdfSrc")
                i.el-icon-document
                p {{ $t('no-doc-hint') }}

            .iframe-wrap(v-else-if="renderedHtml")
                iframe(
                    ref="emailFrame"
                    :srcdoc="renderedHtml"
                    sandbox="allow-same-origin"
                    frameBorder="0"
                    scrolling="yes"
                    @load="onEmailLoad"
                )

            //- Single-page vue-pdf + pagination footer matches the
            //- Purchase Form pdf-preview drawer (no iframe — Firefox
            //- X-Frame-Options safe).
            .pdf-wrap(v-else-if="pdfSrc")
                pdf(
                    ref="pdf-preview"
                    :src="pdfSrc"
                    :page="pdfPage"
                )

            .preview-empty(v-else)
                i.el-icon-document
                p {{ $t('empty-template') }}

    .drawer-footer(v-if="pdfSrc")
        el-button(
            round
            size="mini"
            icon="el-icon-fa-times fas"
            @click="handleClose"
        ) {{ $t('Dismiss') }}
        .preview-pagination(v-if="pdfPages > 1")
            el-pagination(
                small
                layout="prev, pager, next"
                :pager-count="5"
                :page-size="1"
                :total="pdfPages"
                :current-page="pdfPage"
                @current-change="setPdfPage"
            )
        .right-buttons
            el-button(
                circle
                size="mini"
                type="primary"
                :disabled="loading"
                icon="el-icon-fa-download fas"
                @click="downloadPdf"
            )
</template>

<i18n>
{
    "en": {
        "no-doc-hint": "Search and select a record above to load the live preview",
        "Cancellation": "Credit note",
        "credit-note": "Credit note",
        "search-member": "Search by name or e-mail…",
        "search-purchase": "Search by order number or customer…",
        "search-cancellation": "Search by order number or customer…",
        "search-form-entry": "Search by name or e-mail…",
        "search-event": "Search by event name…",
        "opt-custom-id": "ID: {id} (entered manually)",
        "opt-group-recent": "Recent",
        "empty-template": "This template has no content yet",
        "sample-data": "Sample data",
        "no-sample-record": "This shop has no record to preview this template yet. Create one to see the template in action."
    },
    "de": {
        "Preview": "Vorschau",
        "Document": "Beleg",
        "Member": "Mitglied",
        "Order": "Bestellung",
        "Sale": "Verkauf",
        "Event": "Veranstaltung",
        "Form entry": "Formular-Eintrag",
        "Rendering…": "Wird gerendert…",
        "Email": "E-Mail",
        "PDF Letter": "PDF-Brief",
        "Thermal Roll": "Thermodruck",
        "no-doc-hint": "Datensatz suchen und auswählen, um die Live-Vorschau zu laden",
        "Cancellation": "Gutschrift",
        "credit-note": "Gutschrift",
        "Contact Form Notification": "Kontaktformular-Benachrichtigung",
        "search-member": "Nach Name oder E-Mail suchen…",
        "search-purchase": "Nach Bestellnummer oder Kunde suchen…",
        "search-cancellation": "Nach Bestellnummer oder Kunde suchen…",
        "search-form-entry": "Nach Name oder E-Mail suchen…",
        "search-event": "Nach Veranstaltung suchen…",
        "opt-custom-id": "ID: {id} (manuell eingegeben)",
        "opt-group-recent": "Zuletzt erstellt",
        "empty-template": "Diese Vorlage hat noch keinen Inhalt",
        "sample-data": "Beispieldaten",
        "no-sample-record": "Für diesen Shop gibt es noch keinen Datensatz für die Vorschau dieser Vorlage. Legen Sie einen an, um die Vorlage in Aktion zu sehen.",
        "Error rendering preview": "Fehler beim Rendern der Vorschau"
    }
}
</i18n>

<script>
import axios from 'axios';
import pdf from 'vue-pdf';
import PurchaseApi from '@/extensions/shop/store/api/purchase';
import MemberApi from '@/extensions/restrictedArea/store/api/member';
import EntryApi from '@/extensions/forms/store/api/entry';
import EventApi from '@/extensions/eventManager/store/api/event';
import AuthApi from '@/store/api/system/auth';

const PREVIEW_BASE = `${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PREVIEW_ROUTE}`;
const STASH_URL = `${process.env.VUE_APP_API_ENDPOINT}preview-stash`;
const token = () => AuthApi.getToken();
const iframeUrl = (type) => (id) => `${PREVIEW_BASE}${type}/${id}?token=${token()}`;

const RENDER_MAP = {
    'purchase-confirmation:email': {
        fetch: 'purchase',
        getUrl: (id) => PurchaseApi.confirmationRenderingUrl(id, 'email'),
        html: true,
    },
    'invoice:email': {
        fetch: 'purchase',
        getUrl: (id) => PurchaseApi.invoiceRenderingUrl(id, 'email'),
        html: true,
    },
    'credit-note:email': {
        fetch: 'cancellation',
        getUrl: (id) => PurchaseApi.creditNoteRenderingUrl(id, 'email'),
        html: true,
    },
    'purchase-confirmation:pdf': {
        fetch: 'purchase',
        getUrl: (id) => PurchaseApi.confirmationRenderingUrl(id, 'pdf'),
    },
    'invoice:pdf': {
        fetch: 'purchase',
        getUrl: (id) => PurchaseApi.invoiceRenderingUrl(id, 'pdf'),
    },
    'credit-note:pdf': {
        fetch: 'cancellation',
        getUrl: (id) => PurchaseApi.creditNoteRenderingUrl(id, 'pdf'),
    },
    'delivery-note:pdf': {
        fetch: 'purchase',
        getUrl: (id) => PurchaseApi.deliveryNoteRenderingUrl(id),
    },
    'packing-list:pdf': {
        fetch: 'purchase',
        getUrl: (id) => PurchaseApi.packingListRenderingUrl(id),
    },
    'return-form': {
        fetch: 'purchase',
        getUrl: (id) => PurchaseApi.returnFormRenderingUrl(id),
    },
    'return-slip': {
        fetch: 'cancellation',
        getUrl: (id) => PurchaseApi.returnSlipRenderingUrl(id),
    },
    'cash-receipt:thermal': {
        fetch: 'pos-purchase',
        getUrl: (id) => PurchaseApi.receiptRenderingUrl(id),
    },
    'purchase-status-changed:email': {
        fetch: 'purchase',
        getUrl: iframeUrl('purchase-status-changed'),
        html: true,
    },
    'return-received': {
        fetch: 'cancellation',
        getUrl: (id) => PurchaseApi.returnReceivedRenderingUrl(id),
        html: true,
    },
    'return-status-changed': {
        fetch: 'cancellation',
        getUrl: (id) => PurchaseApi.returnStatusChangedRenderingUrl(id),
        html: true,
    },
    'return-reminder': {
        fetch: 'cancellation',
        getUrl: (id) => PurchaseApi.returnReminderRenderingUrl(id),
        html: true,
    },
    'return-lookup-link': {
        fetch: 'purchase',
        getUrl: (id) => PurchaseApi.returnLookupLinkRenderingUrl(id),
        html: true,
    },
    'reset-password:email': {
        fetch: 'member',
        getUrl: iframeUrl('reset-password'),
        html: true,
        synthetic: true,
    },
    'verify-email:email': {
        fetch: 'member',
        getUrl: iframeUrl('verify-email'),
        html: true,
        synthetic: true,
    },
    'form-received:email': {
        fetch: 'form-entry',
        getUrl: iframeUrl('form-received'),
        html: true,
        // Backend can fabricate a sample submission when the edited shop has
        // none of its own (see PreviewController::syntheticFormEntry).
        synthetic: true,
    },
    // Styling a reply must not require an existing ticket, and a real one would
    // put a customer's correspondence in a settings screen — so this renders
    // against a fabricated sample (PreviewController::renderTicketReplyEmail).
    'ticket-reply:email': {
        getUrl: iframeUrl('ticket-reply'),
        html: true,
        synthetic: true,
        syntheticOnly: true,
    },
    // Agent-facing notifications: one document per event, each rendered
    // against a fabricated sample ticket (PreviewController::renderTicketNotification).
    'ticket-notification-created': {
        getUrl: iframeUrl('ticket-notification-created'),
        html: true,
        synthetic: true,
        syntheticOnly: true,
    },
    'ticket-notification-assigned': {
        getUrl: iframeUrl('ticket-notification-assigned'),
        html: true,
        synthetic: true,
        syntheticOnly: true,
    },
    'ticket-notification-requester-replied': {
        getUrl: iframeUrl('ticket-notification-requester-replied'),
        html: true,
        synthetic: true,
        syntheticOnly: true,
    },
    'ticket-notification-changed': {
        getUrl: iframeUrl('ticket-notification-changed'),
        html: true,
        synthetic: true,
        syntheticOnly: true,
    },
    'ticket-notification-stale': {
        getUrl: iframeUrl('ticket-notification-stale'),
        html: true,
        synthetic: true,
        syntheticOnly: true,
    },
    'event-bookings:pdf': {
        fetch: 'event',
        // Vorschau mit der Standard-Spaltenauswahl und QR-Code — genau so, wie
        // der Export aus den Event-Anmeldungen sie erzeugt.
        getUrl: (id) => EventApi.bookingsPdfUrl(id),
    },
    // Styling a coupon should not require having sold one, so both layouts render against an
    // unsaved sample coupon (PreviewController::syntheticCoupon).
    'coupon:pdf': {
        getUrl: iframeUrl('coupon-letter'),
        synthetic: true,
        syntheticOnly: true,
    },
    'coupon:thermal': {
        getUrl: iframeUrl('coupon-till-roll'),
        synthetic: true,
        syntheticOnly: true,
    },
    // A credit-note receipt is tied to a real cancellation, and the picker already knows how to
    // list those.
    'cancellation-receipt:thermal': {
        fetch: 'cancellation',
        getUrl: iframeUrl('cancellation-receipt'),
    },
    // Belongs to a till rather than to a record worth picking — the backend takes the shop's own
    // first point of sale.
    'pending-reservations:thermal': {
        getUrl: iframeUrl('pending-reservation-purchases'),
        synthetic: true,
        syntheticOnly: true,
    },
    // Likewise: a sample deposit against the shop's own till.
    'cash-movement-receipt:thermal': {
        getUrl: iframeUrl('cash-movement-receipt'),
        synthetic: true,
        syntheticOnly: true,
    },
    // Both ticket layouts render from synthetic data: a shop styling its ticket has usually not
    // sold one yet, and the backend builds an unsaved ticket over the shop's own event dates
    // (PreviewController::syntheticEventTicket).
    'event-ticket:email': {
        getUrl: iframeUrl('event-ticket-email'),
        // An email preview returns { document, subject } as JSON, not a document to drop into an
        // iframe - `html: true` is what tells the drawer to render the body itself. Without it the
        // request succeeds and the drawer still reports "Error rendering preview".
        html: true,
        synthetic: true,
        syntheticOnly: true,
    },
    'event-ticket:pdf': {
        getUrl: iframeUrl('event-ticket-letter'),
        synthetic: true,
        syntheticOnly: true,
    },
    'event-ticket:thermal': {
        getUrl: iframeUrl('event-ticket-till-roll'),
        synthetic: true,
        syntheticOnly: true,
    },
    'communication:email': {
        getUrl: iframeUrl('communication'),
        html: true,
        // No record to pick — always rendered against the edited shop's branding
        // with a sample message body (PreviewController::renderCommunicationEmail).
        synthetic: true,
        syntheticOnly: true,
    },
};

export default {
    name: 'DocumentTemplatePreviewDrawer',
    props: {
        visible: { type: Boolean, default: false },
        template: { type: Object, default: null },
        // Already-translated template name passed down from the parent, which
        // holds the full set of template-name translations. Avoids duplicating
        // every template name into this component's own i18n block.
        templateName: { type: String, default: '' },
        // The CMS currently being edited. The preview always renders THIS shop's
        // branding + template partials. The sample-record picker is scoped to
        // this shop so another shop's records are never shown or used.
        cmsId: { type: [String, Number], default: null },
        // The edited shop's id — used to scope purchase/cancellation pickers
        // (which filter by shop rather than cms).
        shopId: { type: [String, Number], default: null },
        // The editor's current (possibly unsaved) template-partial overrides.
        // Stashed server-side before each render so the preview reflects what is
        // on screen right now, not just what has been saved.
        partials: { type: Array, default: () => [] },
    },
    components: { pdf },
    data() {
        return {
            documentId: null,
            entityOptions: [],
            customIdValue: null,
            searching: false,
            loading: false,
            error: null,
            renderedHtml: null,
            pdfUrl: null,
            pdfSrc: null,
            pdfPages: 0,
            pdfPage: 1,
            searchTimeout: null,
            // True when the edited shop has no record of this type and the
            // backend should fabricate a sample one instead.
            useSynthetic: false,
            // True when the shop has no record of this (non-synthetic) type, so
            // there is nothing to preview yet.
            noRecords: false,
        };
    },
    computed: {
        drawerTitle() {
            const name = this.templateName || (this.template && this.template.name) || '';
            return name ? `${this.$t('Preview')}: ${name}` : this.$t('Preview');
        },
        formatLabel() {
            const map = { email: this.$t('Email'), pdf: this.$t('PDF Letter'), thermal: this.$t('Thermal Roll') };
            return map[this.template && this.template.format] || '';
        },
        formatTagType() {
            const map = { email: 'primary', pdf: 'success', thermal: 'warning' };
            return map[this.template && this.template.format] || 'info';
        },
        isThermal() {
            return !!this.template && this.template.format === 'thermal';
        },
        renderConfig() {
            return this.template ? RENDER_MAP[this.template.key] || null : null;
        },
        // Label + search placeholder for the sample-record picker, matched to
        // what is actually being picked (an order, a POS sale, a credit note, a
        // member, a form submission) instead of a generic "Beleg".
        entityLabel() {
            const map = {
                member: this.$t('Member'),
                cancellation: this.$t('Cancellation'),
                'form-entry': this.$t('Form entry'),
                'pos-purchase': this.$t('Sale'),
                purchase: this.$t('Order'),
                event: this.$t('Event'),
            };
            return (this.renderConfig && map[this.renderConfig.fetch]) || this.$t('Document');
        },
        entityPlaceholder() {
            const map = {
                member: this.$t('search-member'),
                cancellation: this.$t('search-cancellation'),
                'form-entry': this.$t('search-form-entry'),
                'pos-purchase': this.$t('search-purchase'),
                purchase: this.$t('search-purchase'),
                event: this.$t('search-event'),
            };
            return (this.renderConfig && map[this.renderConfig.fetch]) || this.$t('search-purchase');
        },
    },
    watch: {
        visible(val) {
            if (!val) return;
            this.reset();
            if (this.renderConfig) this.loadInitialOptions();
        },
        template() {
            if (!this.visible) return;
            this.reset();
            if (this.renderConfig) this.loadInitialOptions();
        },
    },
    methods: {
        handleClose() {
            this.$emit('close');
        },

        reset() {
            this.renderedHtml = null;
            this.pdfUrl = null;
            this.pdfSrc = null;
            this.pdfPages = 0;
            this.pdfPage = 1;
            this.error = null;
            this.loading = false;
            this.documentId = null;
            this.customIdValue = null;
            this.entityOptions = [];
            this.useSynthetic = false;
            this.noRecords = false;
        },

        async loadInitialOptions() {
            const cfg = this.renderConfig;
            if (!cfg) return;
            // Synthetic-only templates have no record to pick — render the sample
            // straight away (no entity dropdown).
            if (cfg.syntheticOnly) {
                this.useSynthetic = true;
                await this.loadPreview();
                return;
            }
            this.searching = true;
            try {
                const items = await this.fetchItems(cfg.fetch, null);
                this.entityOptions = this.mapOptions(cfg.fetch, items);
                if (this.entityOptions.length > 0) {
                    this.useSynthetic = false;
                    this.documentId = this.entityOptions[0].id;
                    await this.loadPreview();
                } else if (cfg.synthetic) {
                    // No own records for this shop → render against fabricated
                    // sample data rather than borrowing another shop's record.
                    this.useSynthetic = true;
                    await this.loadPreview();
                } else {
                    // This shop has no record of this type and the document is
                    // not previewable from synthetic data — show a clear empty
                    // state instead of borrowing another shop's record.
                    this.noRecords = true;
                }
            } catch (e) {
                // ignore
            } finally {
                this.searching = false;
            }
        },

        // Fetch sample records for the picker via the per-domain api modules.
        // Every query is scoped to the edited shop (cmsId for form/member,
        // shopId for purchase/cancellation) so another shop's records are never
        // returned. Scoping is enforced server-side too (the Secure traits +
        // the cms/shop filter's own policy check).
        async fetchItems(type, search) {
            if (type === 'member') {
                return MemberApi.fetchPreviewSamples({ cmsId: this.cmsId, search });
            } else if (type === 'pos-purchase') {
                return PurchaseApi.fetchPreviewSamples({ shopId: this.shopId, search, posOnly: true });
            } else if (type === 'cancellation') {
                return PurchaseApi.fetchPreviewCancellationSamples({ shopId: this.shopId, search });
            } else if (type === 'form-entry') {
                return EntryApi.fetchPreviewSamples({ cmsId: this.cmsId, search });
            } else if (type === 'event') {
                // Der Event-Manager hat keine eigene Sample-Query — die Liste
                // der Veranstaltungen des Shops ist kurz, also lokal filtern.
                const events = await EventApi.fetchList(this.cmsId);
                if (!search) return events;
                const needle = search.toLowerCase();
                return events.filter((event) => (event.name || '').toLowerCase().includes(needle));
            }
            return PurchaseApi.fetchPreviewSamples({ shopId: this.shopId, search });
        },

        mapOptions(type, items) {
            if (type === 'member') return items.map((m) => ({ id: parseInt(m.id), label: this.memberLabel(m) }));
            if (type === 'form-entry') return items.map((e) => ({ id: parseInt(e.id), label: this.formEntryLabel(e) }));
            if (type === 'event')
                return items.map((event) => ({ id: parseInt(event.id), label: event.name || `#${event.id}` }));
            if (type === 'cancellation') {
                const opts = [];
                items.forEach((p) => {
                    const nr = p.invoice_nr || `#${p.id}`;
                    const customer = p.purchaser && p.purchaser.name ? p.purchaser.name : '';
                    (p.cancellations || []).forEach((c) => {
                        opts.push({
                            id: parseInt(c.id),
                            label: `${nr}${customer ? ' — ' + customer : ''} (${this.$t('credit-note')} #${c.id})`,
                        });
                    });
                });
                return opts;
            }
            return items.map((p) => ({ id: parseInt(p.id), label: this.purchaseLabel(p) }));
        },

        searchEntities(query) {
            clearTimeout(this.searchTimeout);
            this.customIdValue = null;
            if (/^\d+$/.test((query || '').trim())) this.customIdValue = parseInt(query.trim());
            if (!query) {
                this.loadInitialOptions();
                return;
            }
            this.searching = true;
            this.searchTimeout = setTimeout(async () => {
                try {
                    const items = await this.fetchItems(this.renderConfig.fetch, query);
                    this.entityOptions = this.mapOptions(this.renderConfig.fetch, items);
                } catch (e) {
                    /* ignore */
                } finally {
                    this.searching = false;
                }
            }, 300);
        },

        async onEntitySelected(val) {
            this.renderedHtml = null;
            this.pdfUrl = null;
            this.pdfSrc = null;
            this.pdfPages = 0;
            this.pdfPage = 1;
            if (!val) return;
            await this.loadPreview();
        },

        // Flag every render started from the document-template management as
        // a non-destructive preview so the backend issues a placeholder
        // invoice number instead of burning a real, sequential one. The
        // purchase form omits this flag and keeps the original
        // issue-on-view behavior.
        previewUrl(url, stashToken) {
            let result = url.includes('?') ? `${url}&template_preview=1` : `${url}?template_preview=1`;
            // Force the edited shop's branding + partials onto the borrowed
            // sample record (see PreviewController::applyEditedCmsContext).
            if (this.cmsId) result += `&preview_cms_id=${this.cmsId}`;
            // Reference the stashed (possibly unsaved) editor content so the
            // render reflects on-screen edits.
            if (stashToken) result += `&preview_stash=${stashToken}`;
            // Fabricate sample data instead of borrowing a real record.
            if (this.useSynthetic) result += `&synthetic=1`;
            return result;
        },

        // Stash the editor's current partial set so the GET-based preview/PDF
        // render can reference it by token. Returns null when there is nothing
        // to stash (no edited CMS or no partials) — the preview then falls back
        // to the CMS's saved partials.
        async stashPartials() {
            if (!this.cmsId || !Array.isArray(this.partials) || !this.partials.length) return null;
            try {
                const payload = { partials: this.partials.map((p) => ({ key: p.key, template: p.template })) };
                const { data } = await axios.post(`${STASH_URL}?token=${token()}`, payload);
                return (data && data.token) || null;
            } catch (e) {
                // A stash failure must not block the preview — fall back to saved partials.
                return null;
            }
        },
        async loadPreview() {
            const cfg = this.renderConfig;
            // A synthetic preview needs no selected record; otherwise require one.
            if (!cfg || (!this.documentId && !this.useSynthetic)) return;
            // Synthetic renders use a placeholder id — the backend ignores it
            // and fabricates the sample record for the edited shop.
            const recordId = this.useSynthetic ? 0 : this.documentId;
            this.loading = true;
            this.error = null;
            this.renderedHtml = null;
            this.pdfUrl = null;
            this.pdfSrc = null;
            this.pdfPages = 0;
            this.pdfPage = 1;
            try {
                // Stash on-screen edits once; both the HTML and PDF paths
                // reference the same token so the render reflects current edits.
                const stashToken = await this.stashPartials();
                if (cfg.getUrl && cfg.html) {
                    // HTML preview via signed URL — fetch and render via
                    // srcdoc so Firefox's X-Frame-Options doesn't block it.
                    const { data } = await axios.get(this.previewUrl(cfg.getUrl(recordId), stashToken));
                    this.renderedHtml = data.document || data.html || data;
                } else if (cfg.getUrl) {
                    // Single-page vue-pdf flow shared by all PDF previews.
                    // Avoids iframes (which Firefox blocks via
                    // X-Frame-Options) and matches the Purchase Form
                    // pdf-preview modal pattern.
                    const url = this.previewUrl(cfg.getUrl(recordId), stashToken);
                    this.pdfUrl = url;
                    const loadingTask = pdf.createLoadingTask(url);
                    const doc = await loadingTask.promise;
                    this.pdfSrc = loadingTask;
                    this.pdfPages = doc.numPages;
                }
            } catch {
                this.error = this.$t('Error rendering preview');
            } finally {
                this.loading = false;
            }
        },

        setPdfPage(page) {
            this.pdfPage = page;
        },

        downloadPdf() {
            if (!this.pdfUrl) return;
            const a = document.createElement('a');
            a.href = `${this.pdfUrl}&download=1`;
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },

        memberLabel(m) {
            const name = m.name || [m.first_name, m.last_name].filter(Boolean).join(' ') || `#${m.id}`;
            return m.email ? `${name} (${m.email})` : name;
        },

        formEntryLabel(e) {
            const name = e.created_by_name || `#${e.id}`;
            return e.created_by_email ? `${name} (${e.created_by_email})` : name;
        },
        purchaseLabel(p) {
            const nr = p.invoice_nr || `#${p.id}`;
            const customer = p.purchaser && p.purchaser.name ? p.purchaser.name : '';
            return customer ? `${nr} — ${customer}` : nr;
        },

        onEmailLoad() {
            this.autoHeight(this.$refs.emailFrame);
        },
        autoHeight(iframe) {
            if (!iframe || !iframe.contentDocument || !iframe.contentDocument.body) return;
            iframe.style.height = Math.max(iframe.contentDocument.body.scrollHeight + 40, 400) + 'px';
        },
    },
};
</script>

<style lang="sass" scoped>
.document-template-preview-drawer
    ::v-deep .el-drawer
        min-width: 480px
        max-width: 92%

    //- Receipt widths, matching the purchase form's own receipt drawer. The min-width above would
    //- otherwise override the 380px size and stretch the roll to more than twice its paper width.
    &.is-thermal
        ::v-deep .el-drawer
            min-width: 320px
            max-width: 380px
    ::v-deep .el-drawer__body
        display: flex
        flex-direction: column
        overflow: hidden
        padding: 0

.drawer-body
    display: flex
    flex-direction: column
    height: 100%
    overflow: hidden

.preview-toolbar
    display: flex
    align-items: center
    gap: 10px
    padding: 10px 20px
    border-bottom: 1px solid #ebeef5
    flex-shrink: 0
    // At receipt width the tags, picker and buttons no longer fit on one line.
    flex-wrap: wrap

    .doc-label
        font-size: 13px
        color: #606266
        white-space: nowrap

    .entity-select
        flex: 1

    .preview-spin
        font-size: 16px
        color: #909399


.preview-content
    flex: 1
    overflow: hidden
    display: flex
    flex-direction: column

.preview-loading
    display: flex
    align-items: center
    justify-content: center
    gap: 10px
    flex: 1
    font-size: 14px
    color: #909399
    i
        font-size: 24px

.preview-error
    margin: 20px

.preview-empty
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    flex: 1
    color: #c0c4cc
    gap: 12px
    i
        font-size: 48px
    p
        margin: 0
        font-size: 14px

.iframe-wrap
    flex: 1
    display: flex
    flex-direction: column
    overflow: hidden
    iframe
        flex: 1
        width: 100%
        border: none

.pdf-wrap
    flex: 1
    overflow-y: auto
    background: #525659
    padding: 16px
    display: flex
    flex-direction: column
    align-items: center
    gap: 8px
    span
        width: 100%
        canvas
            width: 100% !important
            height: auto !important
            display: block
            box-shadow: 0 2px 8px rgba(0,0,0,.4)
</style>
